/**
 * Server mode: everything the server replicates as an entity, drawn by the
 * renderers the offline game already has.
 *
 *  - `spawn` builds the entity from its record (a mob in the page's
 *    EntityManager, which is never ticked here: no AI, spawning or physics),
 *  - `BIN_ENTS` deltas go into a snapshot buffer per entity and are sampled one
 *    tick behind (smooth at 20 Hz; a jump of more than 8 blocks snaps),
 *  - `meta` re-applies the record, `despawn` removes, `collect` flies an item
 *    to whoever picked it up,
 *  - other players keep the smooth track of the `players` rows; their `player`
 *    record adds the held item, armour and hurt flash,
 *  - `mount` seats the local player on an entity and sends `steer` while seated.
 *
 * main.ts calls `tick()` in the game tick instead of the local entity sims and
 * hands the views to EntityRenderer / ItemEntityRenderer / BoatRenderer.
 */

import type { EntityManager } from '../entities/manager';
import type { Mob } from '../entities/mob';
import { applyMobRow, deserializeMob } from '../entities/persist';
import { leashes } from '../entities/leash';
import type { ItemEntityViewEx } from '../entities/itemEntities';
import type { XpOrbView } from '../entities/xp';
import { Boat, SEAT_DROP_PLAYER } from '../entities/vehicles/boat';
import { SEAT_Y } from '../game/mounts';
import { offerFromRow, type Offer } from '../game/trading';
import type { ArrowView, Stack } from '../game/types';
import { INTERP_DELAY_MS, SnapshotBuffer } from '../net/interp';

import {
  BIN_ENTS, decodeEnts, EF_AGGRESSIVE, EF_FIRE, EF_STUCK,
  type ArrowRecord, type BoatRecord, type ItemRecord, type PlayerRecord, type SpawnRow, type XpRecord,
} from '../server/protocol';
import type { ServerPlayerView, ServerSession } from './serverSession';

/** What the replica needs from the page. */
export interface ReplicaDeps {
  srv: ServerSession;
  /** The page's entity manager, used as a container for replicated mobs. */
  entities: EntityManager;
  player: {
    position: { x: number; y: number; z: number };
    yaw: number;
    sitting: boolean;
    rideTick: (() => void) | null;
    rideInput(out: { fwd: number; strafe: number; jump: boolean; jumpTapped: boolean; sneak: boolean }): void;
    rideTo(x: number, y: number, z: number, snap?: boolean): void;
    setBodyYaw(y: number): void;
    resetFall(): void;
  };
  light(x: number, y: number, z: number): number;
  /** A sound the server asked for (`fx` k = 'sound'). */
  sound?(id: string, x: number, y: number, z: number): void;
  /** Items went into our inventory (pop). */
  onPickup?(stack: Stack, n: number): void;
  /** The trade window: offers arrived for window `win` (first time: open the screen). */
  onOffers?(win: number, offers: Offer[], xp: number, level: number, title: string): void;
  /** The server closed the trade window. */
  onTradeClosed?(): void;
  /** Boss bar: f < 0 hides it. */
  onBoss?(name: string, f: number): void;
  onWeather?(rain: number, thunder: number): void;
  onLightning?(x: number, y: number, z: number): void;
  onMount?(on: boolean): void;
}

/** x, y, z, yaw, headYaw, pitch, limbSwing */
const W = 7;
const ANGLES = [3, 4, 5];
const SNAP = 8;

interface Ent {
  id: number;
  kind: string;
  buf: SnapshotBuffer;
  /** Latest state from the server (every group filled in). */
  cur: Float64Array;
  lastPush: number;
  limbAmt: number; swell: number; attackAnim: number; hurtTime: number; deathTime: number; flags: number;
  mob?: Mob;
  item?: ItemEntityViewEx;
  orb?: XpOrbView;
  arrow?: ArrowView;
  boat?: Boat;
  /** Player record: [id, name, x, y, z, yaw, pitch, held, armor[], offhand, flags]. */
  player?: PlayerRecord;
  /** Other kinds (thrown things, tridents, bobbers, clouds, crystals, the dragon, skulls, fireballs): drawn by client/projectiles.ts. */
  other?: OtherView;
  /** Item pickup animation: ticks left, start point and who it flies to. */
  collect?: { left: number; sx: number; sy: number; sz: number; by: number };
}

/** A replicated entity of a kind this module does not draw itself, moved like the rest (one tick behind, px = the place before). */
export interface OtherView {
  id: number; kind: string;
  x: number; y: number; z: number; px: number; py: number; pz: number;
  yaw: number; pitch: number;
  /** BIN_ENTS animation fields (the dragon's wing flap rides in `limb`). */
  limb: number; hurtTime: number; deathTime: number; flags: number;
  /** Ticks drawn so far. */
  age: number;
  /** Latest record (`spawn` / `meta`), see server/protocol.ts for each kind's layout. */
  record: unknown;
}

/** Index of x in the record of a kind (y and z follow); 0 unless listed. */
const POS_AT: Record<string, number> = { thrown: 2, skull: 1, fireball: 1 };

export class ServerReplica {
  private readonly ents = new Map<number, Ent>();
  private readonly byPlayer = new Map<string, Ent>();
  private readonly items: ItemEntityViewEx[] = [];
  private readonly orbs: XpOrbView[] = [];
  private readonly arrows: ArrowView[] = [];
  private boatList: Boat[] = [];
  private readonly s = new Float64Array(W);
  private readonly input = { fwd: 0, strafe: 0, jump: false, jumpTapped: false, sneak: false };
  /** Entity we sit on (0 = on foot). */
  mounted = 0;
  private lastSteer = '';

  constructor(private readonly d: ReplicaDeps) {
    const srv = d.srv;
    srv.on('spawn', (m) => { for (const row of m.e) this.spawn(row); });
    srv.on('meta', (m) => { for (const [id, rec] of m.e) this.meta(id, rec); });
    srv.on('despawn', (m) => { for (const id of m.ids) this.despawn(id); });
    srv.on('collect', (m) => { this.collect(m.id, m.by, m.n); });
    srv.onBinary(BIN_ENTS, (frame) => { this.deltas(frame); });
    srv.on('welcome', () => { this.clear(); });
    let dim = srv.dim;
    srv.on('pos', (m) => { if (m.dim !== dim) { dim = m.dim; this.clear(); } });
    srv.on('mount', (m) => {
      this.mounted = m.on ? m.id : 0;
      this.lastSteer = '';
      const p = d.player;
      p.sitting = m.on;
      p.rideTick = m.on ? () => { this.rideTick(); } : null;
      if (!m.on && m.x !== undefined && m.y !== undefined && m.z !== undefined) { p.rideTo(m.x, m.y, m.z, true); p.resetFall(); }
      d.onMount?.(m.on);
    });
    srv.on('offers', (m) => {
      const offers: Offer[] = [];
      for (const r of m.o) { const o = offerFromRow(r); if (o) offers.push(o); }
      d.onOffers?.(m.win, offers, m.xp, m.level, m.win === tradeWin ? tradeTitle : '');
    });
    let tradeWin = 0, tradeTitle = '';
    srv.on('open', (m) => { if (m.kind === 'trade') { tradeWin = m.win; tradeTitle = m.title ?? ''; } });
    srv.on('closed', (m) => { if (m.win === tradeWin) { tradeWin = 0; d.onTradeClosed?.(); } });
    srv.on('boss', (m) => { d.onBoss?.(m.name, m.f); });
    srv.on('weather', (m) => { d.onWeather?.(m.rain, m.thunder); });
    srv.on('lightning', (m) => { d.onLightning?.(m.x, m.y, m.z); });
    srv.on('fx', (m) => { if (m.k === 'sound' && typeof m.d === 'string') d.sound?.(m.d, m.x, m.y, m.z); });
  }

  // ---- views ---------------------------------------------------------------

  itemViews(): readonly ItemEntityViewEx[] { return this.items; }
  orbViews(): readonly XpOrbView[] { return this.orbs; }
  arrowViews(): readonly ArrowView[] { return this.arrows; }
  get boats(): Boat[] { return this.boatList; }
  kindOf(id: number): string | undefined { return this.ents.get(id)?.kind; }
  /** Entities of a kind, as plain views: the ones this module does not draw itself ('thrown', 'crystal', 'dragon', ...) are moved every tick. */
  others(kind: string): OtherView[] {
    const out: OtherView[] = [];
    for (const e of this.ents.values()) {
      if (e.kind !== kind) continue;
      if (e.other) { out.push(e.other); continue; }
      // a kind drawn elsewhere ('player': the hit test of client/combat.ts): where the server has it now
      const c = e.cur;
      out.push({ id: e.id, kind, x: c[0], y: c[1], z: c[2], px: c[0], py: c[1], pz: c[2], yaw: c[3], pitch: c[5], limb: c[6], hurtTime: e.hurtTime, deathTime: e.deathTime, flags: e.flags, age: 0, record: e.player ?? null });
    }
    return out;
  }
  /** Where an entity is drawn now (a player's eid, a mob), or null. */
  placeOf(id: number): { x: number; y: number; z: number } | null {
    const e = this.ents.get(id);
    if (!e) return null;
    const o = e.mob ?? e.other;
    if (o) return o;
    // players are drawn from the session's own rows; their entity carries the latest place
    return { x: e.cur[0], y: e.cur[1], z: e.cur[2] };
  }

  /** Other players: the session's interpolated rows plus what their entity record says they hold and wear. */
  remotePlayers(out: ServerPlayerView[]): ServerPlayerView[] {
    this.d.srv.remotePlayers(out);
    for (const v of out) {
      const e = this.byPlayer.get(v.id);
      if (!e || !e.player) continue;
      const r = e.player, st = v.state;
      st.heldItem = Number(r[7]) || 0;
      const a = r[8];
      for (let i = 0; i < 4; i++) st.armor[i] = Array.isArray(a) ? Number(a[i]) || 0 : 0;
      st.hurtTime = e.hurtTime;
    }
    return out;
  }

  /** Nearest replicated boat on a ray, or null. */
  boatOnRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, max: number): { id: number; dist: number } | null {
    let best: { id: number; dist: number } | null = null;
    for (const b of this.boatList) {
      const t = b.rayDist(ox, oy, oz, dx, dy, dz, max);
      if (t >= 0 && (!best || t < best.dist)) best = { id: b.id, dist: t };
    }
    return best;
  }

  /** Nearest end crystal or dragon on a ray (a left click strikes it), or null. The server checks the reach itself. */
  bossOnRay(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, max: number): { id: number; dist: number } | null {
    let best: { id: number; dist: number } | null = null;
    for (const e of this.ents.values()) {
      const o = e.other;
      if (!o || (e.kind !== 'crystal' && e.kind !== 'dragon')) continue;
      // crystal: a 2x2x2 box on its base; dragon: the body, generously (the parts are the server's)
      const hw = e.kind === 'crystal' ? 1 : 4, h = e.kind === 'crystal' ? 2 : 4;
      const t = rayBox(ox, oy, oz, dx, dy, dz, o.x - hw, o.y, o.z - hw, o.x + hw, o.y + h, o.z + hw);
      if (t >= 0 && t <= max && (!best || t < best.dist)) best = { id: e.id, dist: t };
    }
    return best;
  }

  // ---- messages ------------------------------------------------------------

  private clear(): void {
    for (const id of [...this.ents.keys()]) this.despawn(id, true);
    this.d.entities.clear();
    this.items.length = 0; this.orbs.length = 0; this.arrows.length = 0; this.boatList = [];
    if (this.mounted) { this.mounted = 0; this.d.player.sitting = false; this.d.player.rideTick = null; this.d.onMount?.(false); }
  }

  private spawn(row: SpawnRow): void {
    const [id, kind, rec] = row as [number, string, unknown];
    if (id === this.d.srv.welcome?.eid) return;
    if (this.ents.has(id)) this.despawn(id, true);
    const e: Ent = {
      id, kind, buf: new SnapshotBuffer(W, ANGLES, 16), cur: new Float64Array(W), lastPush: 0,
      limbAmt: 0, swell: 0, attackAnim: 0, hurtTime: 0, deathTime: 0, flags: 0,
    };
    const c = e.cur;
    const light = this.d.light;
    if (kind === 'mob') {
      const m = deserializeMob(this.d.entities, rec, id, false);
      if (!m) return;
      e.mob = m;
      c[0] = m.x; c[1] = m.y; c[2] = m.z; c[3] = m.yaw; c[4] = m.headYaw; c[5] = m.pitch;
      this.leash(m, rec);
    } else if (kind === 'item') {
      const r = rec as ItemRecord;
      c[0] = r[1]; c[1] = r[2]; c[2] = r[3];
      e.item = { id, stack: r[0] as Stack, x: c[0], y: c[1], z: c[2], px: c[0], py: c[1], pz: c[2], age: 0, light: light(c[0], c[1], c[2]), collected: 0 };
      this.items.push(e.item);
    } else if (kind === 'xp') {
      const r = rec as XpRecord;
      c[0] = r[2]; c[1] = r[3]; c[2] = r[4];
      e.orb = { id, value: r[0], count: r[1], x: c[0], y: c[1], z: c[2], px: c[0], py: c[1], pz: c[2], age: 0, light: light(c[0], c[1], c[2]) };
      this.orbs.push(e.orb);
    } else if (kind === 'arrow') {
      const r = rec as ArrowRecord;
      c[0] = r[0]; c[1] = r[1]; c[2] = r[2];
      const h = Math.hypot(r[3], r[5]);
      c[3] = Math.atan2(-r[3], -r[5]); c[5] = Math.atan2(r[4], h);
      e.arrow = { id, x: c[0], y: c[1], z: c[2], px: c[0], py: c[1], pz: c[2], yaw: c[3], pitch: c[5], stuck: !!r[6], light: 1, trident: !!r[7] };
      this.arrows.push(e.arrow);
    } else if (kind === 'boat') {
      const r = rec as BoatRecord;
      c[0] = r[1]; c[1] = r[2]; c[2] = r[3]; c[3] = r[4];
      e.boat = new Boat(id, r[0], r[1], r[2], r[3], r[4]);
      this.boatList = [...this.boatList, e.boat];
    } else if (kind === 'player') {
      const r = rec as PlayerRecord;
      e.player = r;
      c[0] = r[2]; c[1] = r[3]; c[2] = r[4];
      this.byPlayer.set(r[0], e);
    } else {
      const at = POS_AT[kind] ?? 0;
      if (Array.isArray(rec)) { c[0] = Number(rec[at]) || 0; c[1] = Number(rec[at + 1]) || 0; c[2] = Number(rec[at + 2]) || 0; }
      if (kind === 'dragon' && Array.isArray(rec)) c[3] = Number(rec[3]) || 0;
      e.other = { id, kind, x: c[0], y: c[1], z: c[2], px: c[0], py: c[1], pz: c[2], yaw: c[3], pitch: 0, limb: 0, hurtTime: 0, deathTime: 0, flags: 0, age: 0, record: rec };
    }
    this.ents.set(id, e);
  }

  /** Leads are drawn from the record: ours to our hand, knots to their fence; other players' leads are not drawn. */
  private leash(m: Mob, rec: unknown): void {
    leashes.detach(m, false);
    const l = Array.isArray(rec) ? rec[15] : null;
    if (l === 'p:' + this.d.srv.id) leashes.attach(m, { kind: 'player' });
    else if (Array.isArray(l) && l.length === 3) leashes.attach(m, { kind: 'knot', x: Number(l[0]), y: Number(l[1]), z: Number(l[2]) });
  }

  private meta(id: number, rec: unknown): void {
    const e = this.ents.get(id);
    if (!e) return;
    if (e.mob && Array.isArray(rec)) {
      const m = e.mob;
      const { x, y, z, yaw, headYaw, pitch, px, py, pz } = m;
      const name = m.customName;
      m.customName = '';
      applyMobRow(this.d.entities, m, rec, false);
      if (!m.customName && name && typeof rec[14] === 'string' && rec[14]) m.customName = name;
      // the place comes from the movement stream, not from the record
      m.x = x; m.y = y; m.z = z; m.yaw = yaw; m.headYaw = headYaw; m.pitch = pitch; m.px = px; m.py = py; m.pz = pz;
      this.leash(m, rec);
    } else if (e.item) e.item.stack = (rec as ItemRecord)[0] as Stack;
    else if (e.orb) { const r = rec as XpRecord; e.orb.value = r[0]; e.orb.count = r[1]; }
    else if (e.player) {
      const r = rec as PlayerRecord;
      if (r[0] !== e.player[0]) { this.byPlayer.delete(e.player[0]); this.byPlayer.set(r[0], e); }
      e.player = r;
    } else if (e.other) e.other.record = rec;
  }

  private despawn(id: number, now = false): void {
    const e = this.ents.get(id);
    if (!e) return;
    // an item on its way into a pocket finishes the flight first
    if (!now && e.item && e.collect && e.collect.left > 0) { e.kind = 'gone'; return; }
    this.ents.delete(id);
    if (e.mob) { leashes.detach(e.mob, false); e.mob.removed = true; }
    if (e.item) remove(this.items, e.item);
    if (e.orb) remove(this.orbs, e.orb);
    if (e.arrow) remove(this.arrows, e.arrow);
    if (e.boat) { const b = e.boat; b.removed = true; this.boatList = this.boatList.filter((o) => o !== b); }
    if (e.player && this.byPlayer.get(e.player[0]) === e) this.byPlayer.delete(e.player[0]);
  }

  private collect(id: number, by: number, n: number): void {
    const e = this.ents.get(id);
    if (!e || !e.item) return;
    const it = e.item;
    if (by === this.d.srv.welcome?.eid) this.d.onPickup?.(it.stack, n);
    e.collect = { left: 3, sx: it.x, sy: it.y, sz: it.z, by };
  }

  private deltas(frame: Uint8Array): void {
    const dec = decodeEnts(frame);
    if (!dec) return;
    const now = performance.now();
    for (const dl of dec.ents) {
      const e = this.ents.get(dl.id);
      if (!e) continue;
      const c = e.cur;
      // it stood still for a while: the old place holds until just before this move
      if (now - e.lastPush > 120 && e.buf.length > 0) e.buf.push(now - 50, c);
      let snap = false;
      if (dl.x !== undefined && dl.y !== undefined && dl.z !== undefined) {
        snap = Math.abs(dl.x - c[0]) + Math.abs(dl.y - c[1]) + Math.abs(dl.z - c[2]) > SNAP;
        c[0] = dl.x; c[1] = dl.y; c[2] = dl.z;
      }
      if (dl.yaw !== undefined) { c[3] = dl.yaw; c[4] = dl.headYaw ?? dl.yaw; c[5] = dl.pitch ?? 0; }
      if (dl.limbSwing !== undefined) { c[6] = dl.limbSwing; e.limbAmt = dl.limbAmt ?? 0; }
      if (dl.flags !== undefined) {
        e.swell = dl.swell ?? 0; e.attackAnim = dl.attackAnim ?? 0;
        e.hurtTime = dl.hurtTime ?? 0; e.deathTime = dl.deathTime ?? 0; e.flags = dl.flags;
      }
      if (snap) { e.buf = new SnapshotBuffer(W, ANGLES, 16); this.place(e, c, true); }
      e.buf.push(now, c);
      e.lastPush = now;
    }
  }

  // ---- tick ----------------------------------------------------------------

  /** Put entity `e` at state `v` (px = the place it had, or `v` when snapping). */
  private place(e: Ent, v: Float64Array, snap: boolean): void {
    const o = e.mob ?? e.item ?? e.orb ?? e.arrow ?? e.boat ?? e.other;
    if (!o) return;
    if (snap) { o.px = v[0]; o.py = v[1]; o.pz = v[2]; } else { o.px = o.x; o.py = o.y; o.pz = o.z; }
    o.x = v[0]; o.y = v[1]; o.z = v[2];
  }

  /** One game tick (20 Hz): move everything to where the server had it one tick ago. */
  tick(): void {
    const rt = performance.now() - INTERP_DELAY_MS;
    const v = this.s;
    const light = this.d.light;
    for (const e of this.ents.values()) {
      if (e.collect) { this.flyTo(e); continue; }
      // moves are sent only while something changes: past the newest one the entity stands where it was
      // last told (the buffer would run on along the last velocity - a stuck arrow ended up inside the wall)
      if (rt >= e.lastPush || !e.buf.sample(rt, v)) v.set(e.cur);
      this.place(e, v, false);
      const m = e.mob;
      if (m) {
        m.yaw = v[3]; m.headYaw = v[4]; m.pitch = v[5];
        m.limbSwing = v[6]; m.limbSwingAmount = e.limbAmt;
        m.swell = e.swell;
        m.hurtTime = e.hurtTime; m.deathTime = e.deathTime;
        m.dying = e.deathTime > 0;
        if (m.dying && m.health > 0) m.health = 0;
        m.fireTicks = e.flags & EF_FIRE ? 20 : 0;
        m.aggressive = (e.flags & EF_AGGRESSIVE) !== 0;
        m.swingTicks = e.attackAnim > 0 ? Math.max(1, Math.round(e.attackAnim * 6)) : 0;
      } else if (e.item) { e.item.age++; e.item.light = light(v[0], v[1] + 0.2, v[2]); }
      else if (e.orb) { e.orb.age++; e.orb.light = light(v[0], v[1] + 0.2, v[2]); }
      else if (e.arrow) {
        const a = e.arrow;
        a.yaw = v[3]; a.pitch = v[5]; a.stuck = (e.flags & EF_STUCK) !== 0; a.light = light(v[0], v[1], v[2]);
      } else if (e.boat) {
        const b = e.boat;
        b.pyaw = b.yaw; b.yaw = v[3];
        b.paddlePrev[0] = b.paddle[0]; b.paddlePrev[1] = b.paddle[1];
        b.paddle[0] = v[6]; b.paddle[1] = e.limbAmt;
        b.hurtTime = e.hurtTime;
      } else if (e.other) {
        const o = e.other;
        o.yaw = v[3]; o.pitch = v[5]; o.limb = v[6];
        o.hurtTime = e.hurtTime; o.deathTime = e.deathTime; o.flags = e.flags;
        o.age++;
      }
    }
    this.d.entities.syncViews();
  }

  private flyTo(e: Ent): void {
    const c = e.collect, it = e.item;
    if (!c || !it) return;
    c.left--;
    let tx = it.x, ty = it.y, tz = it.z;
    if (c.by === this.d.srv.welcome?.eid) { const p = this.d.player.position; tx = p.x; ty = p.y + 0.9; tz = p.z; }
    else { const t = this.ents.get(c.by); if (t) { tx = t.cur[0]; ty = t.cur[1] + 0.9; tz = t.cur[2]; } }
    const k = (3 - c.left) / 3;
    it.px = it.x; it.py = it.y; it.pz = it.z;
    it.x = c.sx + (tx - c.sx) * k; it.y = c.sy + (ty - c.sy) * k; it.z = c.sz + (tz - c.sz) * k;
    it.collected = 3 - c.left;
    if (c.left <= 0) {
      e.collect = undefined;
      it.collected = 0;
      if (e.kind === 'gone') this.despawn(e.id, true);
      else { it.x = it.px = e.cur[0]; it.y = it.py = e.cur[1]; it.z = it.pz = e.cur[2]; }
    }
  }

  // ---- riding ----------------------------------------------------------------

  /** Player.rideTick while seated: sit on the mount where it is drawn, and tell the server what the keys say. */
  private rideTick(): void {
    const p = this.d.player;
    const e = this.ents.get(this.mounted);
    const i = this.input;
    p.rideInput(i);
    const msg = `${i.fwd},${i.strafe},${i.jump ? 1 : 0},${i.sneak ? 1 : 0}`;
    if (msg !== this.lastSteer) {
      this.lastSteer = msg;
      this.d.srv.send({ t: 'steer', f: i.fwd, s: i.strafe, j: i.jump, sn: i.sneak });
    }
    if (!e) return;
    if (e.boat) {
      p.rideTo(e.boat.x, e.boat.y + SEAT_DROP_PLAYER + 0.45, e.boat.z);
      p.setBodyYaw(e.boat.yaw);
    } else if (e.mob) {
      p.rideTo(e.mob.x, e.mob.y + (SEAT_Y[e.mob.type] ?? 0.8), e.mob.z);
      p.setBodyYaw(e.mob.yaw);
    }
    p.resetFall();
  }
}

function remove<T>(list: T[], item: T): void {
  const i = list.indexOf(item);
  if (i >= 0) list.splice(i, 1);
}

/** Distance along a ray to a box, or -1 (slab test; 0 when the origin is inside). */
function rayBox(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number {
  let t0 = 0, t1 = Infinity;
  const o = [ox, oy, oz], d = [dx, dy, dz], lo = [x0, y0, z0], hi = [x1, y1, z1];
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]) < 1e-9) { if (o[i] < lo[i] || o[i] > hi[i]) return -1; continue; }
    let a = (lo[i] - o[i]) / d[i], b = (hi[i] - o[i]) / d[i];
    if (a > b) { const s = a; a = b; b = s; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return -1;
  }
  return t0;
}
